/* eslint-disable testing-library/no-debugging-utils */
import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import * as braze from '@braze/web-sdk'

// Types for Braze SDK configuration
interface BrazeConfig {
  apiKey: string
  endpoint: string
  enableLogging?: boolean
}

interface UserTraits {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
}

type EventProperties = Record<string, unknown>

declare global {
  interface Window {
    braze?: typeof braze
    evergreenBraze?: {
      initializeBrazeSDK: (_config: BrazeConfig) => void
      identifyUser: (_userId: string, _traits?: UserTraits) => void
      trackEvent: (_eventName: string, _properties?: EventProperties) => void
      isReady: () => boolean
    }
  }
}

const log = new Logger('Consumer Engagement Service - Braze')

let instance: ConsumerEngagementService | undefined

class ConsumerEngagementService {
  private isInitialized = false
  private jobQueue: (() => void)[] = []
  private userId: string | undefined

  constructor() {
    if (!instance) {
      instance = this
    }

    // Verify Braze SDK loaded (from npm import)
    if (typeof window !== 'undefined' && !window.braze) {
      log.warn('Braze SDK not available on window after import')
    } else {
      log.debug('Braze SDK available on window.braze')
    }

    // Expose YOUR wrapper methods on separate namespace for RudderStack to invoke
    if (typeof window !== 'undefined') {
      window.evergreenBraze = {
        initializeBrazeSDK: this.initializeBrazeSDK,
        identifyUser: this.identifyUser,
        trackEvent: this.trackEvent,
        isReady: this.getIsReady,
      }

      log.debug('window.evergreenBraze methods exposed for RudderStack integration')
    }

    // Listening for the authenticated notification to be fired when the user is authenticated
    // Once the user is authenticated, we will update the user id and change the user in braze
    const authenticatedListener = (data: unknown): void => {
      const eventData = data as { detail: { userId?: string } }
      if (this.userId !== eventData.detail.userId) {
        this.userId = eventData.detail.userId
        this.enqueue(() => {
          log.debug('Queueing change user', NOTIFICATIONS.AUTH.AUTHENTICATED)
          if (eventData.detail.userId) {
            log.debug('Update identity: ', eventData.detail)
            braze.changeUser(eventData.detail.userId)
            // Refresh content cards when user identity changes during session
            log.debug('Refreshing content cards user identity changed')
            braze.requestContentCardsRefresh(this.contentCardsRefreshed, this.contentCardsRefreshedError)
          }
        })
      }
    }
    notifications.on(NOTIFICATIONS.AUTH.AUTHENTICATED, authenticatedListener)

    // Listening for the Sign Out notification to be fired when the user signs out
    // This is to ensure that the data is flushed to the server
    const requestImmediateDataFlushListener = (): void => {
      log.debug('requestImmediateDataFlush listener fired: ', NOTIFICATIONS.AUTH.SIGN_OUT)
      this.enqueue(() => {
        braze.requestImmediateDataFlush()
      })
    }
    notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, requestImmediateDataFlushListener)

    // ===== CLEAR IDENTITY LISTENER =====
    // Listen for identity context being cleared
    const clearIdentityListener = (): void => {
      log.debug('Clear identity listener fired:', NOTIFICATIONS.EVENTS.CLEAR_IDENTITY_CONTEXT)
      this.enqueue(() => {
        // Flush data before logout to ensure all events are sent
        log.debug('Flushing Braze data after identity cleared')
        braze.requestImmediateDataFlush()
        // Note: We don't call changeUser here because we want to maintain
        // anonymous tracking. The Analytics Service handles reset() for RudderStack.
      })
    }
    notifications.on(NOTIFICATIONS.EVENTS.CLEAR_IDENTITY_CONTEXT, clearIdentityListener)
  }

  private enqueue(action: () => void): void {
    if (this.isInitialized) {
      log.debug('Firing job')
      action()
    } else {
      log.debug('Queuing job as service is not initialized', {
        isInitialized: this.isInitialized,
        userId: this.userId,
        typeofWindow: typeof window,
      })
      this.jobQueue.push(action)
    }
  }

  private flushQueue(): void {
    log.debug('Flushing queue', this.jobQueue)
    while (this.jobQueue.length > 0) {
      const action = this.jobQueue.shift()
      action?.()
    }
  }

  private contentCardsRefreshed = (): void => {
    log.debug('Content cards refreshed')
  }

  private contentCardsRefreshedError = (): void => {
    log.error('Error refreshing content cards')
  }

  // PUBLIC METHODS - Exposed on window.evergreenBraze for RudderStack to invoke

  public initializeBrazeSDK = (config: BrazeConfig): void => {
    log.debug('Initializing Braze SDK', { apiKey: config.apiKey, endpoint: config.endpoint })

    if (this.isInitialized) {
      log.warn('Braze SDK already initialized')
      return
    }

    try {
      // Initialize Braze SDK with config
      braze.initialize(config.apiKey, {
        baseUrl: config.endpoint,
        enableLogging: config.enableLogging,
        allowUserSuppliedJavascript: true,
      })

      braze.automaticallyShowInAppMessages()

      // Subscribe to real-time content card updates for dynamic refresh
      braze.subscribeToContentCardsUpdates((cards: unknown) => {
        log.debug('Content cards updated', cards)
      })

      // If we already have a userId, identify the user
      if (this.userId) {
        log.debug('Service Initializing - changeUser', this.userId)
        braze.changeUser(this.userId)
        // Force refresh to get user-specific content cards after identity change
        braze.requestContentCardsRefresh(this.contentCardsRefreshed, this.contentCardsRefreshedError)
      }

      // Open session
      braze.openSession()

      const contentCardsData = braze.getCachedContentCards()
      log.debug('Cached content cards after initialization', { cards: contentCardsData?.cards })

      this.isInitialized = true
      log.debug('Braze SDK initialized successfully')

      // Dispatch notification
      notifications.dispatch('BRAZE.INIT')

      // Flush queued actions
      this.flushQueue()
    } catch (error) {
      log.error('Failed to initialize Braze SDK', error)
      throw error
    }
  }

  public identifyUser = (userId: string, traits?: UserTraits): void => {
    this.enqueue(() => {
      log.debug('Identifying user', { userId })

      braze.changeUser(userId)

      const user = braze.getUser()
      if (user) {
        if (traits?.email) {
          user.setEmail(traits.email)
        }

        if (traits?.firstName) {
          user.setFirstName(traits.firstName)
        }

        if (traits?.lastName) {
          user.setLastName(traits.lastName)
        }

        if (traits?.phone) {
          user.setPhoneNumber(traits.phone)
        }
      }

      notifications.dispatch('BRAZE.USER_IDENTIFIED', { userId })
    })
  }

  public trackEvent = (eventName: string, properties?: EventProperties): void => {
    this.enqueue(() => {
      log.debug('Tracking event', { eventName, properties })
      braze.logCustomEvent(eventName, properties)
      notifications.dispatch('BRAZE.EVENT_TRACKED', { eventName })
    })
  }

  public getIsReady = (): boolean => {
    return this.isInitialized && typeof window !== 'undefined' && Boolean(braze)
  }

  // Content card methods exposed on window.evergreenBraze
  public hideContentCards = (parentNode?: Element | null): void => {
    // Not going to queue to avoid race condition with showContentCards
    if (this.isInitialized) {
      braze.hideContentCards(parentNode)
    }
  }

  // Not Queueing this as it is not critical to the service, just exposing the method to outer services
  public getCachedContentCards = (): braze.Card[] => {
    const contentCardsData = braze.getCachedContentCards()
    return contentCardsData?.cards ?? []
  }

  // This allows components to invoke this method to show content cards
  public showContentCards = (...args: unknown[]): void => {
    this.enqueue(() => {
      try {
        braze.requestContentCardsRefresh(this.contentCardsRefreshed, this.contentCardsRefreshedError)
        // Get cached content cards to log what cards are available
        const contentCardsData = braze.getCachedContentCards()
        const cards = contentCardsData?.cards ?? []

        log.debug('Showing content cards', {
          totalCards: cards.length,
          cards: cards.map((card: any) => ({
            id: card.id,
            title: card.title,
            description: card.description,
            cardType: card.cardType,
            extras: card.extras,
          })),
        })

        braze.showContentCards(...(args as [Element | null]))
      } catch (error) {
        log.error('Error retrieving or showing content cards', error)
        // Still attempt to show content cards even if logging fails
        braze.showContentCards(...(args as [Element | null]))
      }
    })
  }

  // This is a custom function to process content cards and set target attributes based on extras for a specific card
  // Hopefully this will be removed depending on Rudderstack's ability to handle this during initialization
  // Process content cards to set target attributes based on extras
  // We will be able to refactor the ConsumerEngagementFeed complexity away if this is successful
  processContentCardTargets = (cards: braze.Card[]): void => {
    log.debug('processContentCardTargets', cards)
    try {
      cards.forEach((cardData: any) => {
        const linkTarget = cardData.extras?.['link-target'] ?? '_self'
        const cardUrl = cardData.url
        const cardId = cardData.id

        log.debug('Processing card:', {
          id: cardId,
          title: cardData.title,
          url: cardUrl,
          linkTarget,
          extras: cardData.extras,
        })

        // Find the DOM element for this card
        let cardElement = document.querySelector(`[data-ab-card-id="${cardId}"]`)

        // Fallback: find by content matching
        if (!cardElement) {
          const allCards = document.querySelectorAll('.ab-card')
          allCards.forEach((cardEl) => {
            const titleElement = cardEl.querySelector('.ab-title')
            const descriptionElement = cardEl.querySelector('.ab-description')

            if (
              (cardData.title && titleElement?.textContent?.includes(cardData.title)) ||
              (cardData.description && descriptionElement?.textContent?.includes(cardData.description))
            ) {
              cardElement = cardEl
              log.debug(`Found matching card by content: ${cardData.title ?? 'unknown'}`)
            }
          })
        }

        if (cardElement && cardUrl) {
          log.debug('Card element found, adding click listener:', { cardElement, cardUrl, linkTarget })

          // Add click listener to the card element itself
          cardElement.addEventListener(
            'click',
            (e) => {
              e.preventDefault()
              e.stopPropagation()
              // Track card click for Braze analytics before custom navigation
              braze.logContentCardClick(cardData)

              if (linkTarget === '_blank') {
                window.open(cardUrl, '_blank', 'noopener,noreferrer')
                log.debug('Opened card URL in new tab:', cardUrl)
              } else {
                window.location.href = cardUrl
                log.debug('Navigated to card URL:', cardUrl)
              }
            },
            true,
          )
          log.debug(`Added click handler for card ${cardId} with target "${linkTarget}"`)
        } else {
          log.warn(`Could not find card element or missing URL for card ${cardId}`)
        }
      })
    } catch (error) {
      log.error('Error processing content card targets', error)
    }
  }
}

export const consumerEngagementService = new ConsumerEngagementService()
